import type { BadgeProps } from '@opengoal/ui';
import {
  ContractDeadlineDtoState,
  ContractStatus,
  ContractType,
  ContractsControllerListV1Quick,
  ContractsControllerRenewalsV1Bucket,
  IncentiveBasis,
  IncentiveMetric,
  SignatureProvider,
  ContractSigningParty,
  ContractDocumentKind,
} from '@/api/generated/model';

/**
 * The vocabularies, in display order, drawn from the generated model so that a
 * value added on the backend is a compile error here rather than a blank chip.
 */
export const CONTRACT_STATUSES = [
  ContractStatus.draft,
  ContractStatus.in_review,
  ContractStatus.out_for_signature,
  ContractStatus.executed,
  ContractStatus.expired,
  ContractStatus.terminated,
] as const;

export const CONTRACT_TYPES = [
  ContractType.representation,
  ContractType.employment,
  ContractType.loan,
  ContractType.transfer,
  ContractType.third_party,
  ContractType.commercial,
  ContractType.mandate,
  ContractType.agent_subcontract,
  ContractType.commission,
] as const;

export const QUICK_FILTERS = [
  ContractsControllerListV1Quick.all,
  ContractsControllerListV1Quick.negotiation,
  ContractsControllerListV1Quick.signature,
  ContractsControllerListV1Quick.active,
  ContractsControllerListV1Quick.expiring,
] as const;

export const RENEWAL_BUCKETS = [
  ContractsControllerRenewalsV1Bucket.expired,
  ContractsControllerRenewalsV1Bucket.within_30,
  ContractsControllerRenewalsV1Bucket.within_60,
  ContractsControllerRenewalsV1Bucket.within_90,
  ContractsControllerRenewalsV1Bucket.within_180,
  ContractsControllerRenewalsV1Bucket.within_365,
] as const;

export const INCENTIVE_METRICS = Object.values(IncentiveMetric);
export const INCENTIVE_BASES = Object.values(IncentiveBasis);
export const SIGNATURE_PROVIDERS = Object.values(SignatureProvider);
export const SIGNING_PARTIES = Object.values(ContractSigningParty);
export const DOCUMENT_KINDS = Object.values(ContractDocumentKind);

export const STATUS_VARIANT: Record<ContractStatus, BadgeProps['variant']> = {
  [ContractStatus.draft]: 'secondary',
  [ContractStatus.in_review]: 'warning',
  [ContractStatus.out_for_signature]: 'info',
  [ContractStatus.executed]: 'success',
  [ContractStatus.expired]: 'warning',
  [ContractStatus.terminated]: 'danger',
};

export const DEADLINE_VARIANT: Record<ContractDeadlineDtoState, BadgeProps['variant']> = {
  [ContractDeadlineDtoState.upcoming]: 'secondary',
  [ContractDeadlineDtoState.due]: 'warning',
  [ContractDeadlineDtoState.passed]: 'danger',
  [ContractDeadlineDtoState.reached]: 'success',
};

/** Statuses the backend lets a contract be deleted in. */
export const DELETABLE_STATUSES: readonly ContractStatus[] = [
  ContractStatus.draft,
  ContractStatus.in_review,
];

/** Statuses a signature round can be opened from. */
export const SIGNABLE_STATUSES: readonly ContractStatus[] = [
  ContractStatus.draft,
  ContractStatus.in_review,
];

/** The upload constraints of the `contract-document` scope, mirrored for the dropzone. */
export const DOCUMENT_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.webp';
export const DOCUMENT_MAX_BYTES = 25 * 1024 * 1024;
